import Hero from "../components/Hero";
import hotelImg from "../assests/images/hotel-bg.jpg";
import CTASection from "../components/CTASection";
import { Helmet } from "react-helmet-async";
import {
  FaHotel, FaPlane, FaCar, FaMapMarkedAlt, FaHeadset,
  FaShieldAlt, FaUtensils, FaPassport, FaArrowRight,
  FaCheck, FaGlobeAmericas,
} from "react-icons/fa";

const services = [
  { icon: FaHotel, title: "Hotel Booking", description: "Find thoughtfully selected stays at competitive prices, with a smooth booking experience from start to finish.", link: "https://cheapdubaihotels.com/", tag: "Stay" },
  { icon: FaPlane, title: "Flight Booking", description: "Explore convenient flight options and flexible travel choices for journeys near and far.", link: "https://southtravels.com/flights", tag: "Fly" },
  { icon: FaPassport, title: "Visa Services", description: "Get practical visa guidance and document assistance for a more confident application process.", link: "https://southtravels.com/", tag: "Prepare" },
  { icon: FaMapMarkedAlt, title: "Tour Packages", description: "Discover memorable destinations and curated experiences designed around the way you travel.", link: "https://tourismindubai.com/", tag: "Explore" },
  { icon: FaUtensils, title: "Food & Dining", description: "Find inviting dining experiences, from local favourites to special occasions.", link: "https://dubaicitytour.com/", tag: "Taste" },
  { icon: FaCar, title: "Car Rentals", description: "Enjoy convenient rental options for comfortable, independent travel around Dubai.", link: "https://southtravels.com/car-rental-from-dubai", tag: "Drive" },
];

const benefits = [
  { icon: FaShieldAlt, title: "Secure & reliable", description: "A booking experience designed with care, clarity and peace of mind." },
  { icon: FaHeadset, title: "24/7 assistance", description: "Travel support when you need a helping hand." },
  { icon: FaHotel, title: "Selected stays", description: "Explore hotel options in sought-after destinations." },
  { icon: FaGlobeAmericas, title: "Everything connected", description: "Hotels, flights and experiences in one convenient place." },
];

export default function Services() {
  return (
    <>
      <Helmet>
        <title>Travel & Hotel Services | Beach Hotels Dubai</title>
        <meta name="description" content="Explore affordable hotel booking, budget travel planning, holiday packages, visa assistance, car rentals and travel services with Beach Hotels Dubai." />
        <link rel="canonical" href="https://cheapdubaihotels.com/services" />
        <meta property="og:title" content="Travel & Hotel Services | Beach Hotels Dubai" />
        <meta property="og:description" content="Explore hotel bookings, flights, visa services, tours, dining and car rentals with Beach Hotels Dubai." />
        <meta property="og:url" content="https://cheapdubaihotels.com/services" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Travel & Hotel Services | Beach Hotels Dubai" />
        <meta name="twitter:description" content="Explore hotel bookings, flights, visa services, tours, dining and car rentals with Beach Hotels Dubai." />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <main className="min-h-screen bg-[#F7F9FC] text-slate-900">
        <Hero variant="inner" title="Our Services" subtitle="Thoughtful travel services for every part of your journey." backgroundImage={hotelImg} />

        <section className="relative overflow-hidden bg-white py-14 sm:py-20">
          <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
            <div className="grid items-end gap-7 lg:grid-cols-[1fr_0.65fr] lg:gap-20">
              <div>
                <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#E46B38]">
                  <span className="h-px w-8 bg-[#E46B38]" /> Discover what we offer
                </span>
                <h1 className="mt-5 max-w-2xl text-4xl font-bold leading-[1.12] tracking-tight text-[#123E53] sm:text-5xl lg:text-[3.5rem]">
                  All the essentials for <span className="text-[#E46B38]">better travel.</span>
                </h1>
              </div>
              <p className="max-w-md text-base leading-8 text-slate-500">
                From the perfect stay to the journey itself, explore useful travel services that make planning feel effortless.
              </p>
            </div>

            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:mt-14 lg:grid-cols-3">
              {services.map(({ icon: Icon, title, description, link, tag }, index) => (
                <a key={title} href={link} target="_blank" rel="noopener noreferrer"
                  className="group relative flex min-h-[290px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_5px_25px_rgba(18,62,83,0.035)] transition-all duration-300 hover:-translate-y-1 hover:border-[#B9D3DC] hover:shadow-[0_18px_45px_rgba(18,62,83,0.11)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#0B6E8E]">
                  <div className="flex items-start justify-between">
                    <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#EAF4F7] text-xl text-[#0B6E8E] transition-colors group-hover:bg-[#0B6E8E] group-hover:text-white"><Icon aria-hidden="true" /></span>
                    <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">{String(index + 1).padStart(2, "0")} / {tag}</span>
                  </div>
                  <h2 className="mt-9 text-xl font-bold tracking-tight text-[#123E53]">{title}</h2>
                  <p className="mt-3 flex-1 text-sm leading-7 text-slate-500">{description}</p>
                  <div className="mt-7 flex items-center justify-between border-t border-slate-100 pt-5 text-sm font-semibold text-[#0B6E8E]">
                    <span>Explore service</span>
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F1F6F8] transition-all group-hover:translate-x-1 group-hover:bg-[#E46B38] group-hover:text-white"><FaArrowRight aria-hidden="true" /></span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#123E53] py-16 text-white sm:py-20">
          <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
            <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFB88C]">Why choose us</span>
                <h2 className="mt-4 text-3xl font-bold leading-tight tracking-tight !text-white sm:text-4xl">
                  Your journey, <span className="!text-white">made simpler.</span>
                </h2>
                <p className="mt-5 max-w-md text-sm leading-7 text-white/70">We bring practical travel services together so you can spend less time arranging and more time enjoying.</p>
                <div className="mt-7 flex items-center gap-2 text-sm font-medium text-white/90"><FaCheck className="text-[#FFB88C]" aria-hidden="true" /> Simple, convenient planning</div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {benefits.map(({ icon: Icon, title, description }) => (
                  <div key={title} className="rounded-2xl border border-white/15 bg-white/[0.06] p-6 transition-colors hover:bg-white/[0.1]">
                    <Icon className="text-xl text-[#FFB88C]" aria-hidden="true" />
                    <h3 className="mt-5 text-base font-bold !text-white">{title}</h3>
                    <p className="mt-2 text-sm leading-6 text-white/65">{description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        <CTASection />
      </main>
    </>
  );
}
